import { setIdHeader, setToken } from "@/clients/open_api_injection";
import { UsersService } from "@/clients/services/UsersService";
import { LocalStorageKeys } from "@/constants/local-storage-keys";
import { QueryKeys } from "@/constants/query-keys";
import { trackOnboardingV5AccountCreated } from "@/features/onboarding/analytics";
import type { CurrentUser } from "@/hooks/requests/user/use-get-user";
import { UserIdSchema } from "@/schemas/user-schema";
import { markSignedInBefore } from "@/utils/has-signed-in-before";
import { decodeJWT } from "@/utils/jwt";
import { setLocalStorageJson } from "@/utils/local-storage";
import { setUserInfo } from "@/utils/sentry/tools";
import { setMixpanelUser } from "@/utils/mixpanel";
import { setSmartlookUser } from "@/utils/smartlook";
import { useAuth, useClerk, useUser } from "@clerk/clerk-react";
import { useQueryClient } from "@tanstack/react-query";

const toError = (error: unknown, fallbackMessage: string) => {
  if (error instanceof Error) {
    return error;
  }

  return new Error(fallbackMessage);
};

export const useRegisterFinalization = () => {
  const { user } = useUser();
  const { getToken } = useAuth();
  const { signOut } = useClerk();
  const queryClient = useQueryClient();

  const finalizeRegistration = async (email: string) => {
    try {
      const token = await getToken();
      if (!token) {
        throw new Error("No token found");
      }

      const decodedToken = decodeJWT(token);
      const userId = user?.id || decodedToken?.sub;
      if (!userId) {
        throw new Error("No user ID found");
      }

      setToken(token);
      setIdHeader(userId);

      const createUserRequest = UserIdSchema.parse({
        email,
        isOnboardingCompleted: false,
        id: userId,
      });

      await UsersService.createUserApiV1UsersPost({
        email: createUserRequest.email,
        isOnboardingCompleted: createUserRequest.isOnboardingCompleted ?? true,
        id: createUserRequest.id,
      });

      const { data } = await UsersService.loginApiV1UsersLoginPost(userId);
      const dataStorage = UserIdSchema.parse({
        email,
        isOnboardingCompleted: data.isOnboardingCompleted,
        id: userId,
      });

      setLocalStorageJson(LocalStorageKeys.USER_KEY, dataStorage);
      markSignedInBefore();
      queryClient.setQueryData<CurrentUser>([QueryKeys.USER], (prev) => ({
        ...(prev ?? {}),
        id: userId,
        isOnboardingCompleted: data.isOnboardingCompleted,
      }));
      // Clerk's `setActive` flips `isSignedIn` to true *before* this function
      // writes USER_KEY, which can cause `useGetSubscriptionStatus` to
      // auto-enable and run its queryFn against an empty USER_KEY — the
      // queryFn throws, the query enters error state, and with
      // `retry: false` it never recovers. Force a refetch now that
      // USER_KEY is in place.
      await queryClient.invalidateQueries({
        queryKey: [QueryKeys.SUBSCRIPTION_STATUS],
      });
      setUserInfo(dataStorage);
      await setSmartlookUser(userId, email);
      setMixpanelUser(userId, email);
      trackOnboardingV5AccountCreated(userId);
    } catch (error) {
      await signOut();
      localStorage.removeItem(LocalStorageKeys.ACCESS_TOKEN_KEY);
      localStorage.removeItem(LocalStorageKeys.USER_KEY);
      throw toError(error, "Failed to finalize registration.");
    }
  };

  return {
    finalizeRegistration,
  };
};
